import type { DiagnosticBand } from "../types/diagnostic";

/**
 * The stage ladder, read from the diagnostic's own authored bands.
 *
 * Nothing here hardcodes Build/Grow/Lead/Scale: two companies in the directory
 * may have answered different instruments, and an instrument can be republished
 * with a different ladder. Bands are stored in `minScore` order conceptually
 * but arrive in whatever order the payload lists them, so every helper sorts
 * first.
 */

/** Band labels lowest stage first — index 0 is the earliest stage. */
export function bandLabels(bands: readonly DiagnosticBand[]): string[] {
  return [...bands].sort((a, b) => a.minScore - b.minScore).map((b) => b.label);
}

/** Stage index to its authored label. */
export function stageLabel(stage: number | null, labels: string[]): string {
  if (stage == null) return "—";
  return labels[stage] ?? `Stage ${stage}`;
}

/**
 * The inverse: a band label back to its position on the ladder.
 *
 * The PDF needs this because it prints the stage **recorded at completion**
 * — a label, not an index — and the stage pill is coloured by ladder position.
 * Colouring from a recomputed `gate.finalStage` instead would let the pill
 * disagree with the word printed on it.
 *
 * Returns null for a label this diagnostic does not define, which reads as
 * "unplaced" rather than guessing a position.
 */
export function stageIndexFromBands(
  label: string | null,
  bands: readonly Pick<DiagnosticBand, "label" | "minScore">[],
): number | null {
  if (label == null) return null;
  const index = [...bands]
    .sort((a, b) => a.minScore - b.minScore)
    .findIndex((b) => b.label === label);
  return index === -1 ? null : index;
}
